import { OWNED_SHEET_KEY } from "@/lib/office-helpers";

/**
 * Sheets that must never be deleted by cleanup functions.
 */
export const PROTECTED_SHEETS = ["Resultados"];

/**
 * Sheet name mapping for each project.
 * Used to clean up worksheets when navigating away.
 */

export const PROJECT_SHEETS: Record<string, string[]> = {
  project1: [
    "Asistencia de Visitantes",
    "Actividades-Guardabosques",
    "Programas por Parque",
  ],
  project2: [
    "Mercado Agricultores",
    "Ingresos de Alquiler",
    "Ventas Proyectadas (Prox. ano)",
  ],
  project3: [
    "Inscripciones Escuela de Esquí",
    "Precios de Esquí en Utah",
  ],
  project4: [
    "Departamento de Ventas",
    "Calculadora de Envío",
    "Calculadora de Pago",
    "Exportaciones de Grano",
  ],
  project5: [
    "Principales Patrocinadores",
    "Expositores",
    "Horario de Sesiones",
    "Horario Diario",
  ],
  project6: [
    "Ventas",
    "Envíos",
    "Precios de artículos especiales",
    "Extensión de almacen",
    "Resumen gráfico",
  ],
  project7: [
    "Ventas actuales T1",
    "Objetivo de ventas T1",
    "Inventario de alimentos",
    "Tabla dinámica",
  ],
  project8: [
    "Ciudades capitales del mundo",
    "Top 15 Ciudades",
  ],
  project9: [
    "Costos",
    "Ganancias",
  ],
  project10: [
    "Encontrar un instructor",
    "Cursos",
  ],
  project11: [
    "Tarifa de alquiler",
  ],
  project12: [
    "Tiendas de campaña",
    "Pesca",
  ],
  project13: [
    "Lista de precios",
  ],
  project14: [
    "Ventas T1",
    "Catálogo",
  ],
  project15: [
    "Trimestre 1",
    "Trimestre 2",
  ],
  project16: [
    "Bonos de empleados",
    "Partes",
  ],
  project17: [
    "Descargas",
  ],
  project18: [
    "Inventario de alimentos",
    "Alimentos orgánicos",
  ],
  project19: [
    "Costos",
    "Ganancias",
  ],
  project20: [
    "Salas",
  ],
  project21: [
    "Vehículos",
    "Vendido",
  ],
  project22: [
    "Trimestre 1",
    "Trimestre 2",
    "Gráfico Trimestre 2",
  ],
  project23: [
    "Horas de empleados",
    "Tiempo promedio de llamada",
  ],
  "funciones-buscarx": [
    "BX Catálogo",
    "BX Pedidos",
    "BX Consultas",
  ],
  "funciones-si": ["LG Cobranza"],
  "funciones-y-o-no": ["LG Créditos"],
  "funciones-si-conjunto": ["LG Desempeño"],
  "funciones-si-error": ["LG Reporte"],
  "funciones-es-validacion": ["LG Validación"],
  "funciones-anidadas": ["LG Nómina"],
  "funciones-sumarsiconjunto": ["AG Ventas"],
  "funciones-contarsiconjunto": ["AG Tickets"],
  "funciones-promediosiconjunto": ["AG Inmuebles"],
  "funciones-tablero-agregacion": ["AG Tablero"],
  "funciones-buscarv": ["BR Precios"],
  "funciones-indice-coincidir": ["BR Directorio"],
  "funciones-coincidirx": ["BR Envíos"],
  "funciones-cotizador": ["BR Cotizador"],
  "funciones-extraer": ["TX Inventario"],
  "funciones-limpiar": ["TX Clientes"],
  "funciones-antes-despues": ["TX Correos"],
  "funciones-dividir": ["TX Dividir"],
  "funciones-erp": ["TX ERP"],
  "funciones-componentes-fecha": ["FE Facturas"],
  "funciones-contratos": ["FE Contratos"],
  "funciones-antiguedad": ["FE Personal"],
  "funciones-dias-habiles": ["FE Proyectos"],
  "funciones-turnos": ["FE Turnos"],
  "funciones-redondeo": ["MT Precios"],
  "funciones-sumaproducto": ["MT Inventario"],
  "funciones-estadistica": ["MT Salarios"],
  "funciones-jerarquia": ["MT Concurso"],
  "funciones-proveedores": ["MT Proveedores"],
  "funciones-elegir": ["AV Ventas"],
  "funciones-tomar": ["AV Sucursales"],
  "funciones-apilar": ["AV Consolidado"],
  "funciones-encol": ["AV Guardias"],
  "funciones-reporte-trimestral": ["AV Reporte"],
  "funciones-filtrar": ["MD Talento"],
  "funciones-unicos": ["MD Clientes"],
  "funciones-ordenar": ["MD Ranking"],
  "funciones-secuencia": ["MD Calendario"],
  "funciones-reporte-matrices": ["MD Reporte"],
};

export const ENIGMA_SHEETS = [
  "¡COMIENZA!",
  "Piramide Dorado",
  "Barras azules",
  "Azul y dorado",
  "Verde y amarillo",
  "Tubería",
  "¡TERMINASTE!",
];

export const DASHBOARD_SHEETS = ["Datos", "Resumen", "Dashboard"];

/**
 * Sheet names a project may have left behind, including the mis-encoded
 * "VehÃ­culos" name that older builds of project 21 created.
 */
function projectSheetNames(projectId: string): string[] {
  const sheets = PROJECT_SHEETS[projectId] ?? [];
  return projectId === "project21" ? [...sheets, "VehÃ­culos"] : sheets;
}

/**
 * Delete add-in-created sheets in two round trips. Only sheets tagged with
 * OWNED_SHEET_KEY are removed, so user sheets that happen to share a name
 * (e.g. "Ventas") survive. `names` limits the sweep; omit it to remove
 * every add-in sheet.
 */
async function deleteOwnedSheets(
  ctx: Excel.RequestContext,
  names?: string[]
): Promise<void> {
  const sheets = ctx.workbook.worksheets;
  sheets.load("items/name,items/visibility");
  await ctx.sync();

  const candidates = sheets.items.filter(
    (ws) =>
      !PROTECTED_SHEETS.includes(ws.name) &&
      (!names || names.includes(ws.name))
  );
  if (candidates.length === 0) return;

  const tags = candidates.map((ws) =>
    ws.customProperties.getItemOrNullObject(OWNED_SHEET_KEY)
  );
  await ctx.sync();

  const doomed = candidates.filter((_, i) => !tags[i].isNullObject);
  if (doomed.length === 0) return;

  // Excel refuses to delete the last visible sheet; leave a blank one.
  const visibleLeft = sheets.items.filter(
    (ws) =>
      ws.visibility === Excel.SheetVisibility.visible && !doomed.includes(ws)
  );
  if (visibleLeft.length === 0) {
    sheets.add().activate();
  }

  for (const ws of doomed) ws.delete();
  await ctx.sync();
}

/**
 * Refuse to start a project when the workbook already has a user sheet with
 * one of its names. Checked before anything is created so a failed start
 * never leaves half-built, untagged sheets behind.
 */
export async function assertNoSheetConflicts(projectId: string): Promise<void> {
  await Excel.run(async (ctx) => {
    const names = projectSheetNames(projectId);
    const found = names.map((name) =>
      ctx.workbook.worksheets.getItemOrNullObject(name)
    );
    await ctx.sync();

    const tags = found.map((ws) =>
      ws.isNullObject
        ? null
        : ws.customProperties.getItemOrNullObject(OWNED_SHEET_KEY)
    );
    await ctx.sync();

    const conflict = names.find((_, i) => tags[i] && tags[i].isNullObject);
    if (conflict) {
      throw new Error(
        `Este libro ya tiene una hoja llamada "${conflict}". Para no modificar tu información, abre un libro en blanco para hacer esta práctica.`
      );
    }
  });
}

/**
 * Tag the sheets a project just created so cleanup can recognise them.
 */
export async function tagProjectSheets(projectId: string): Promise<void> {
  await Excel.run(async (ctx) => {
    const found = projectSheetNames(projectId).map((name) =>
      ctx.workbook.worksheets.getItemOrNullObject(name)
    );
    await ctx.sync();

    for (const ws of found) {
      if (!ws.isNullObject) ws.customProperties.add(OWNED_SHEET_KEY, "1");
    }
    await ctx.sync();
  });
}

/**
 * Whether this workbook still holds sheets from a practice session — used to
 * drop a remembered session when the task pane reopens on a different file.
 */
export async function hasOwnedSheets(): Promise<boolean> {
  return Excel.run(async (ctx) => {
    const sheets = ctx.workbook.worksheets;
    sheets.load("items/name");
    await ctx.sync();

    const tags = sheets.items.map((ws) =>
      ws.customProperties.getItemOrNullObject(OWNED_SHEET_KEY)
    );
    await ctx.sync();
    return tags.some((tag) => !tag.isNullObject);
  });
}

/**
 * Delete all sheets belonging to a specific project.
 */
export async function cleanupProject(projectId: string): Promise<void> {
  const names = projectSheetNames(projectId);
  if (names.length === 0) return;

  try {
    await Excel.run((ctx) => deleteOwnedSheets(ctx, names));
  } catch {
    // Not in Excel — silently continue
  }
}

/**
 * Delete ALL add-in project sheets (clean workspace).
 */
export async function cleanupAllProjects(): Promise<void> {
  try {
    await Excel.run((ctx) => deleteOwnedSheets(ctx));
  } catch {
    // Not in Excel — silently continue
  }
}

/**
 * Delete sheets by name, tagged or not. Only Enigma and Dashboard use this;
 * they don't tag their sheets yet (see EXTRA_TOOLS_ENABLED).
 */
async function safeDelete(
  ctx: Excel.RequestContext,
  name: string
): Promise<void> {
  const ws = ctx.workbook.worksheets.getItemOrNullObject(name);
  await ctx.sync();
  if (!ws.isNullObject) {
    ws.delete();
    await ctx.sync();
  }
}

export async function cleanupAllEnigma(): Promise<void> {
  try {
    await Excel.run(async (ctx) => {
      for (const name of ENIGMA_SHEETS) {
        await safeDelete(ctx, name);
      }
    });
  } catch {
    // Not in Excel â€” silently continue
  }
}

export async function cleanupDashboard(): Promise<void> {
  try {
    await Excel.run(async (ctx) => {
      for (const name of DASHBOARD_SHEETS) {
        await safeDelete(ctx, name);
      }
    });
  } catch {
    // Not in Excel â€” silently continue
  }
}

